/**
 * Hero - Premium Hero Component (Challenge Design System)
 *
 * Uses the same design system as ChallengePage + CockpitV3 jumbotron:
 * - VIDEO/IMAGE hero background on left
 * - Game tiles sidebar on right
 * - Gradient overlay with configurable colors
 * - Responsive typography
 */

import React, { useRef, useEffect, useCallback, useState } from "react";
import {
    HubHeroSection,
    SurfaceThemeColors,
    HubFollowMode,
    ResponsiveTypography,
    SocialProfileResponse,
} from "@/hooks/hub/useGetHub";
import useWindowSize from "@/hooks/useWindowSize";
import { IoMdPersonAdd } from "react-icons/io";
import { IoChevronBack } from "react-icons/io5";
import { FaTwitch, FaYoutube } from "react-icons/fa";
import { SiKick } from "react-icons/si";
import { useNavigate } from "react-router";
import { SocialLinks } from "@/widgets/SocialLinks";
import { FollowButton } from "@/widgets/FollowButton";
import "./Hero.scss";
import {formatCompactNumber} from "@/utils/formatCompactNumber";

const STREAM_PLATFORM_PRIORITY = ["TWITCH", "KICK", "YOUTUBE"] as const;

function getPrimaryStreamSocial(socials?: SocialProfileResponse[]): SocialProfileResponse | undefined {
    if (!socials?.length) return undefined;
    for (const p of STREAM_PLATFORM_PRIORITY) {
        const match = socials.find(s => s.platform === p && typeof s.followers === "number" && s.followers > 0);
        if (match) return match;
    }
    return undefined;
}

function StreamPlatformIcon({ platform }: { platform: string }): React.ReactElement | null {
    if (platform === "TWITCH") return <FaTwitch className="hero__stat-platform-icon hero__stat-platform-icon--twitch" />;
    if (platform === "KICK") return <SiKick className="hero__stat-platform-icon hero__stat-platform-icon--kick" />;
    if (platform === "YOUTUBE") return <FaYoutube className="hero__stat-platform-icon hero__stat-platform-icon--youtube" />;
    return null;
}

interface GameTile {
    id: string;
    name: string;
    thumbnailUrl?: string;
    questCount?: number;
}

interface HeroProps {
    heroSection?: HubHeroSection;
    hubName: string;
    surfaceSlug?: string;
    /** When set, follow UI uses Drop follow APIs instead of Hub */
    dropSlug?: string;
    /** Hub MongoDB ObjectId — used as entityId for Click tracking */
    surfaceId?: string;
    themeColors?: SurfaceThemeColors;
    games?: GameTile[];
    socials?: SocialProfileResponse[];
    onGameClick?: (gameId: string) => void;
    /** Fallback header/banner image from creator's social profile */
    creatorHeaderImageUrl?: string;
    /** Fallback profile image from creator's social profile */
    creatorProfileImageUrl?: string;
    /** Whether to show the CTA button (should only show if there's something to enter) */
    showCtaButton?: boolean;
    /** Hub type - determines layout style (CREATOR uses profile-centric layout) */
    hubType?: string;
    /** Description from hub config (separate from heroSection.description for creator hubs) */
    hubDescription?: string;
    /** Follow mode — OPEN, APPROVAL, or INVITE_ONLY */
    followMode?: HubFollowMode;
    /** Number of followers */
    followerCount?: number;
    /** Callback when follow button requires login */
    onLoginRequired?: () => void;
    /** Referrer user ID for follow attribution (from ?hubRef= query param) */
    referralUserId?: string;
    /** Callback when invite icon is clicked */
    onInviteClick?: () => void;
    /** Enable parallax scroll effect on hero background */
    heroParallax?: boolean;
    /** Hero layout — "split" creates a two-column layout with a widget zone on the right */
    heroLayout?: "default" | "split";
    /** React node rendered in the split layout widget zone (e.g. countdown, My Position stats) */
    splitWidget?: React.ReactNode;
    /*
     * State-aware compact mode. When the creator has any LIVE stream the page
     * pivots stream-primary: this hero shrinks (slim banner, smaller avatar,
     * description hidden) so the player below dominates. When offline the hero
     * occupies the full storefront treatment.
     */
    isCompact?: boolean;
    /** Set when any featuredStream is live — drives the pulse dot on the avatar. */
    hasLiveStream?: boolean;
    /** Discord widget `presenceCount` — rendered as the "online" stat. */
    discordOnlineCount?: number;
}

// Default typography values (matching Challenge page)
const DEFAULT_TYPOGRAPHY = {
    title: {
        font: "owners-narrow, sans-serif",
        desktopSize: "6.25rem",
        tabletSize: "3.75rem",
        mobileSize: "3.25rem",
    },
    description: {
        font: "Inter, sans-serif",
        desktopSize: "1.125rem",
        tabletSize: "1rem",
        mobileSize: "0.875rem",
    },
};

// Breakpoints matching Challenge page
const BREAKPOINTS = {
    mobile: 576,
    tablet: 992,
};

/*
 * CompactAbout — single-line preview of the creator's About text with an
 * inline "More ▾" toggle. Collapsed by default so the description doesn't
 * push the player below the fold; expanded shows the full block until the
 * user collapses again.
 */
const CompactAbout: React.FC<{ text: string }> = ({ text }) => {
    const [expanded, setExpanded] = useState(false);
    if (!text) return null;
    return (
        <div className={`hero__about${expanded ? " hero__about--expanded" : ""}`}>
            <span className="hero__about-text">{text}</span>
            <button
                type="button"
                className="hero__about-toggle"
                onClick={() => setExpanded(v => !v)}
                aria-expanded={expanded}
            >
                {expanded ? "Less ▴" : "More ▾"}
            </button>
        </div>
    );
};

export const Hero = ({
    heroSection,
    hubName,
    surfaceSlug,
    dropSlug,
    surfaceId,
    themeColors,
    games = [],
    socials = [],
    onGameClick,
    creatorHeaderImageUrl,
    creatorProfileImageUrl,
    showCtaButton = false,
    hubType,
    hubDescription,
    followMode,
    followerCount,
    onLoginRequired,
    referralUserId,
    onInviteClick,
    heroParallax = false,
    heroLayout = "default",
    splitWidget,
    isCompact = false,
    hasLiveStream = false,
    discordOnlineCount,
}: HeroProps) => {
    const videoRef = useRef<HTMLVideoElement>(null);
    const heroRef = useRef<HTMLElement>(null);
    const windowSize = useWindowSize();
    const navigate = useNavigate();

    /*
     * Parallax scroll effect — background media moves at 0.4x scroll speed
     * Stops updating past 600px scroll for performance. Respects prefers-reduced-motion.
     */
    const handleParallaxScroll = useCallback(() => {
        if (heroRef.current && window.scrollY < 600) {
            heroRef.current.style.setProperty("--parallax-y", `${window.scrollY * 0.4}px`);
        }
    }, []);

    useEffect(() => {
        if (!heroParallax) {
            // Reset to 0 so a previously-scrolled offset doesn't stick
            if (heroRef.current) {
                heroRef.current.style.setProperty("--parallax-y", "0px");
            }
            return;
        }

        /*
         * prefers-reduced-motion is read once per effect cycle. If the user
         * toggles their OS preference mid-session the parallax won't update
         * until the next mount/prop change — acceptable trade-off vs. adding
         * a matchMedia listener for a rare interaction.
         */
        const prefersReducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
        if (prefersReducedMotion) return;

        window.addEventListener("scroll", handleParallaxScroll, { passive: true });
        return () => window.removeEventListener("scroll", handleParallaxScroll);
    }, [heroParallax, handleParallaxScroll]);

    // Determine current breakpoint
    const getBreakpoint = (): "mobile" | "tablet" | "desktop" => {
        if (windowSize[0] <= BREAKPOINTS.mobile) return "mobile";
        if (windowSize[0] <= BREAKPOINTS.tablet) return "tablet";
        return "desktop";
    };
    const breakpoint = getBreakpoint();

    // Get responsive font size
    const getResponsiveSize = (
        typography: ResponsiveTypography | undefined,
        defaults: { desktopSize: string; tabletSize: string; mobileSize: string },
    ): string => {
        if (!typography) {
            return defaults[`${breakpoint}Size`];
        }
        switch (breakpoint) {
            case "mobile":
                return typography.mobileSize || defaults.mobileSize;
            case "tablet":
                return typography.tabletSize || defaults.tabletSize;
            default:
                return typography.desktopSize || defaults.desktopSize;
        }
    };

    // Hero media - use creatorHeaderImageUrl as fallback for auto-generated hubs
    const heroType = heroSection?.heroType || "IMAGE";
    const heroImageUrl = heroSection?.heroImageUrl || heroSection?.heroImageExternalUrl || creatorHeaderImageUrl;
    const videoUrl = heroSection?.videoUrl;
    const videoPosterUrl = heroSection?.videoPosterImageUrl;

    // Text content
    const title = heroSection?.title || hubName;
    const description = heroSection?.description || "";

    // CTA button
    const ctaText = heroSection?.ctaText || "ENTER NOW";
    const ctaBackgroundColor = heroSection?.ctaBackgroundColor || themeColors?.primary || "#7B7B7B";
    const ctaTextColor = heroSection?.ctaTextColor || "#ffffff";

    /*
     * Gradient overlay defaults are LIGHT — just enough to fade the very
     * bottom edge of the banner into the strip below for readability.
     * Heavy overlay (the old defaults of opacity 80 + coverage 50) made
     * creator banner art barely visible: bottom half of the banner was
     * effectively a solid black wash. Brand hubs that want a strong dim
     * for over-banner text can opt in by setting heroSection fields
     * explicitly.
     */
    const showGradient = heroSection?.showGradientOverlay !== false;
    const gradientColor = heroSection?.gradientEndColor || "#000000";
    const gradientOpacity = (heroSection?.gradientOpacity ?? 25) / 100;
    const gradientCoverage = heroSection?.gradientCoverage ?? 15;
    const gradientDirection = heroSection?.gradientDirection || "to top";

    // Dynamic styles
    const titleStyle: React.CSSProperties = {
        fontFamily: heroSection?.titleTypography?.font || DEFAULT_TYPOGRAPHY.title.font,
        fontSize: getResponsiveSize(heroSection?.titleTypography, DEFAULT_TYPOGRAPHY.title),
        fontWeight: 900,
        lineHeight: 0.95,
        textTransform: "uppercase",
        whiteSpace: "pre-line",
    };

    const descriptionStyle: React.CSSProperties = {
        fontFamily: heroSection?.descriptionTypography?.font || DEFAULT_TYPOGRAPHY.description.font,
        fontSize: getResponsiveSize(heroSection?.descriptionTypography, DEFAULT_TYPOGRAPHY.description),
    };

    const handleCtaClick = () => {
        // Scroll to first section
        const navElement = document.querySelector(".nav");
        if (navElement) {
            navElement.scrollIntoView({ behavior: "smooth" });
        }
    };

    // Render background media
    const renderMedia = () => {
        if (heroType === "VIDEO" && videoUrl) {
            return (
                <video
                    ref={videoRef}
                    className="hero__video"
                    autoPlay
                    muted
                    loop
                    playsInline
                    poster={videoPosterUrl}
                >
                    <source src={videoUrl} type="video/mp4" />
                </video>
            );
        }
        if (heroImageUrl) {
            return (
                <img
                    src={heroImageUrl}
                    alt={hubName}
                    className="hero__image"
                />
            );
        }
        return <div className="hero__placeholder" />;
    };

    const hasGames = games.length > 0;

    // Build gradient style — direction is configurable (e.g. "to top", "270deg", "to right")
    const gradientStyle = showGradient ? (() => {
        const opacityHex = Math.round(gradientOpacity * 255)
            .toString(16)
            .padStart(2, "0");
        return {
            background: `linear-gradient(${gradientDirection}, ${gradientColor} 0%, ${gradientColor}${opacityHex} ${gradientCoverage}%, transparent 100%)`,
        };
    })() : undefined;

    // Creator hubs use a different layout - profile pic overlapping banner, name/description/socials below
    const isCreatorHub = hubType === "CREATOR";
    const creatorDescription = hubDescription || heroSection?.description || "";

    /*
     * Creator Hub Layout — wireframe-spec compact identity strip
     * Reference: gs://wireframes-devourplay/couragejd/index.html
     */
    if (isCreatorHub) {
        const primaryColor = themeColors?.primary || "#FCC42C";
        const cssVars = { "--hub-primary": primaryColor } as React.CSSProperties;
        /*
         * Back affordance — most users land on a creator hub via the
         * Drop-In directory and have no obvious way back when the
         * sidebar collapses on mobile. `navigate(-1)` covers the common
         * case; if there's no history entry it falls back to the
         * directory index so the button is never a dead-end.
         */
        const goBack = () => {
            if (window.history.length > 1) navigate(-1);
            else navigate("/drop-in");
        };
        return (
            <section
                ref={heroRef}
                className={`hero hero--creator${heroParallax ? " hero--parallax" : ""}`}
                style={cssVars}
            >
                {/* Banner — decoration only, no overlays */}
                <div className="hero__banner">
                    {heroImageUrl
                        ? <img src={heroImageUrl} alt="" className="hero__banner-image" />
                        : <div className="hero__banner-placeholder" />}
                    {showGradient && <div className="hero__banner-gradient" style={gradientStyle} />}

                    {/* Back affordance — anchored top-left of the banner */}
                    <button
                        type="button"
                        className="hero__back"
                        onClick={goBack}
                        aria-label="Go back"
                    >
                        <IoChevronBack />
                    </button>
                </div>

                {/* Compact profile strip — avatar bridges banner, all functional data in one row */}
                <div className="hero__strip">
                    <div className="hero__strip-inner">
                        <div className="hero__avatar">
                            {creatorProfileImageUrl
                                ? <img src={creatorProfileImageUrl} alt={hubName} />
                                : <div className="hero__avatar-placeholder">{hubName.substring(0, 2).toUpperCase()}</div>}
                            {hasLiveStream && <div className="hero__live-dot" aria-label="Live" />}
                        </div>

                        <div className="hero__identity">
                            <div className="hero__name">{title}</div>
                            <div className="hero__stats">
                                {(() => {
                                    const streamSocial = getPrimaryStreamSocial(socials);
                                    if (streamSocial?.followers) {
                                        return (
                                            <span className="hero__stat-platform">
                                                <StreamPlatformIcon platform={streamSocial.platform} />
                                                <strong>{formatCompactNumber(streamSocial.followers)}</strong> followers
                                            </span>
                                        );
                                    }
                                    if (followerCount && followerCount > 0) {
                                        return <span><strong>{formatCompactNumber(followerCount)}</strong> followers</span>;
                                    }
                                    return null;
                                })()}
                                {typeof discordOnlineCount === "number" && discordOnlineCount > 0 &&
                                    <>
                                        <span className="hero__divider">·</span>
                                        <span className="hero__online">● {formatCompactNumber(discordOnlineCount)} online</span>
                                    </>
                                }
                            </div>
                        </div>

                        {(socials.length > 0 || surfaceSlug || onInviteClick) &&
                            <div className="hero__actions">
                                {socials.length > 0 &&
                                    <SocialLinks socials={socials} size="small" showFollowers={false} />
                                }
                                {surfaceSlug &&
                                    <FollowButton
                                        slug={surfaceSlug}
                                        dropSlug={dropSlug}
                                        surfaceId={surfaceId}
                                        followMode={followMode}
                                        followerCount={followerCount}
                                        hubName={hubName}
                                        onLoginRequired={onLoginRequired}
                                        referralUserId={referralUserId}
                                    />
                                }
                                {onInviteClick &&
                                    <button
                                        className="hero__invite-btn"
                                        onClick={onInviteClick}
                                        title="Invite Friends"
                                        type="button"
                                    >
                                        <IoMdPersonAdd size={16} />
                                    </button>
                                }
                            </div>
                        }
                    </div>

                    {/*
                     * Description compacted to a single line with a "More" reveal —
                     * a multi-line block under the strip was eating fold space above
                     * the player. Wireframe doesn't show description at all in the
                     * identity strip area; this is the smallest concession that
                     * still surfaces the about-text without dominating.
                     */}
                    {creatorDescription && !isCompact &&
                        <CompactAbout text={creatorDescription} />
                    }
                </div>
            </section>
        );
    }

    // Default Hub Layout - Featured hero with optional game tiles sidebar
    return (
        <section className={`hero${heroParallax ? " hero--parallax" : ""}`} ref={heroRef}>
            <div className="hero__layout">
                {/* Featured Hero - Left Side (75%) */}
                <div className="hero__featured">
                    {/* Background Media */}
                    <div className="hero__media">
                        {renderMedia()}
                    </div>

                    {/* Gradient Overlay */}
                    {showGradient &&
                        <div
                            className="hero__gradient"
                            style={gradientStyle}
                        />
                    }

                    {/* Content Overlay */}
                    <div className="hero__content">
                        <h1 className="hero__title owners-narrow_bold" style={titleStyle}>
                            {title}
                        </h1>

                        {description &&
                            <p className="hero__description" style={descriptionStyle}>
                                {description}
                            </p>
                        }

                        {/* CTA Button - only show if explicitly enabled (e.g., has challenge/competition) */}
                        {showCtaButton &&
                            <button
                                className="hero__cta"
                                onClick={handleCtaClick}
                                style={{
                                    backgroundColor: ctaBackgroundColor,
                                    color: ctaTextColor,
                                }}
                            >
                                {ctaText}
                            </button>
                        }

                        {/* Social Links */}
                        {socials.length > 0 &&
                            <div className="hero__socials">
                                <SocialLinks
                                    socials={socials}
                                    size="medium"
                                    showFollowers={true}
                                />
                            </div>
                        }

                        {/* Follow Button + Invite Icon */}
                        <div className="hero__follow">
                            <div className="hero__action-row">
                                {surfaceSlug &&
                                    <FollowButton
                                        slug={surfaceSlug}
                                        dropSlug={dropSlug}
                                        followMode={followMode}
                                        followerCount={followerCount}
                                        hubName={hubName}
                                        onLoginRequired={onLoginRequired}
                                        referralUserId={referralUserId}
                                    />
                                }
                                {onInviteClick &&
                                    <button
                                        className="hero__invite-btn"
                                        onClick={onInviteClick}
                                        title="Invite Friends"
                                    >
                                        <IoMdPersonAdd size={18} />
                                    </button>
                                }
                            </div>
                        </div>
                    </div>
                </div>

                {/* Right Side — Widget zone (split layout) or Games Grid (default) */}
                {heroLayout === "split" && splitWidget
                    ? <div className="hero__widget-zone">
                        {splitWidget}
                    </div>
                    : hasGames &&
                        <div className="hero__games">
                            {games.map((game) =>
                                <div
                                    key={game.id}
                                    className="hero__game-card"
                                    onClick={() => onGameClick?.(game.id)}
                                >
                                    {game.thumbnailUrl
                                        ? <img
                                            src={game.thumbnailUrl}
                                            alt={game.name}
                                            className="hero__game-thumbnail"
                                        />
                                        : <div className="hero__game-placeholder" />
                                    }
                                </div>)}
                        </div>
                }
            </div>
        </section>
    );
};
